import { Component } from "@theme/component";
import {
  CartAddEvent,
  CartErrorEvent,
  CartGroupedSections,
  CartUpdateEvent,
  ThemeEvents,
  VariantUpdateEvent,
} from "@theme/events";
import { fetchConfig } from "@theme/utilities";
import { storefrontEvents } from "@theme/storefront-events";

/**
 * A custom element that manages an add to cart button.
 *
 * @typedef {object} AddToCartRefs
 * @property {HTMLButtonElement} addToCartButton - The add to cart button.
 * @extends Component<AddToCartRefs>
 */
export class AddToCartComponent extends Component {
  requiredRefs = ["addToCartButton"];

  /** @type {AbortController | null} */
  #abortController = null;

  connectedCallback() {
    // A fresh controller on every connect: the previous one was aborted when the element was removed.
    this.#abortController = new AbortController();
    const { signal } = this.#abortController;

    // Listen for cart add events to remove loading state
    document.addEventListener(CartAddEvent.eventName, this.#onCartAddDone, { signal });

    // Listen for cart error events to remove loading state
    document.addEventListener(CartErrorEvent.eventName, this.#onCartAddDone, { signal });

    super.connectedCallback();
  }

  disconnectedCallback() {
    this.#abortController?.abort();
    super.disconnectedCallback();
  }

  /**
   * Disables the add to cart button.
   */
  disable() {
    this.refs.addToCartButton.disabled = true;
  }

  /**
   * Enables the add to cart button.
   */
  enable() {
    this.refs.addToCartButton.disabled = false;
  }

  /**
   * Adds loading class to the add to cart button.
   */
  addLoading() {
    this.refs.addToCartButton.classList.add("btn--loading");
    this.refs.addToCartSpinner.classList.remove("hidden");
  }

  /**
   * Removes loading class from the add to cart button.
   */
  removeLoading() {
    this.refs.addToCartButton.classList.remove("btn--loading");
    this.refs.addToCartSpinner.classList.add("hidden");
  }

  /**
   * Handles cart add completion (success or error).
   * Removes loading state from the button.
   */
  #onCartAddDone = () => {
    this.removeLoading();
  };
}

if (!customElements.get("add-to-cart-component")) {
  customElements.define("add-to-cart-component", AddToCartComponent);
}

/**
 * A custom element that manages a product form.
 *
 * @typedef {object} ProductFormRefs
 * @property {HTMLInputElement} variantId - The form input for submitting the variant ID.
 * @property {AddToCartComponent | undefined} addToCartButtonContainer - The add to cart button container element.
 * @property {HTMLElement | undefined} addToCartTextError - The add to cart text error.
 * @property {HTMLElement | undefined} acceleratedCheckoutButtonContainer - The accelerated checkout button container element.
 * @property {HTMLElement} liveRegion - The live region.
 *
 * @extends Component<ProductFormRefs>
 */
class ProductFormComponent extends Component {
  requiredRefs = ["variantId", "liveRegion"];
  /** @type {AbortController | null} */
  #abortController = null;

  /** @type {number | undefined} */
  #timeout;

  connectedCallback() {
    super.connectedCallback();

    // A fresh controller on every connect: the previous one was aborted when the element was removed.
    this.#abortController = new AbortController();
    const { signal } = this.#abortController;
    const target = this.closest(".shopify-section, dialog, product-card");
    target?.addEventListener(ThemeEvents.variantUpdate, this.#onVariantUpdate, {
      signal,
    });
    target?.addEventListener(ThemeEvents.variantSelected, this.#onVariantSelected, { signal });

    // Capture phase: runs before the click listener of the Shopify accelerated checkout button.
    this.addEventListener("click", this.#validateBeforeAcceleratedCheckout, { capture: true, signal });
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#abortController?.abort();
  }

  /**
   * Accelerated checkout buttons skip the form's constraint validation (e.g. required line item properties).
   * Only the regular "Buy it now" button can be blocked here: wallet buttons (Shop Pay, PayPal, ...) render
   * inside cross-origin iframes, so their clicks never reach the theme and cannot be intercepted.
   *
   * @param {MouseEvent} event
   */
  #validateBeforeAcceleratedCheckout = (event) => {
    const container = this.refs.acceleratedCheckoutButtonContainer;
    if (!container || !(event.target instanceof Node) || !container.contains(event.target)) return;

    const form = this.querySelector("form");
    if (!form || form.checkValidity()) return;

    event.preventDefault();
    event.stopImmediatePropagation();
    form.reportValidity();
  };

  /**
   * Dispatches {@link CartGroupedSections} and returns unique section ids for cart `sections` params.
   *
   * @returns {string[]}
   */
  #gatherGroupedSectionIds() {
    const sections = [];
    document.dispatchEvent(new CartGroupedSections(sections));
    return [...new Set(sections)];
  }

  /**
   * Handles the submit event for the product form.
   *
   * @param {Event} event - The submit event.
   */
  handleSubmit(event) {
    const { addToCartTextError } = this.refs;
    // Stop default behaviour from the browser
    event.preventDefault();

    if (this.#timeout) clearTimeout(this.#timeout);

    // Check if the add to cart button is disabled and do an early return if it is
    if (this.refs.addToCartButtonContainer?.refs.addToCartButton?.getAttribute("disabled") === "true") return;

    // Add loading state to the add to cart button
    this.refs.addToCartButtonContainer?.addLoading();

    // Send the add to cart information to the cart
    const form = this.querySelector("form");

    if (!form) throw new Error("Product form element missing");

    const formData = new FormData(form);

    const groupedSectionIds = this.#gatherGroupedSectionIds();
    if (groupedSectionIds.length > 0) {
      formData.append("sections", groupedSectionIds.join(","));
    }

    const fetchCfg = fetchConfig("javascript", { body: formData });

    const linesUpdate = storefrontEvents.start(this, "CartLinesUpdateEvent", {
      action: "add",
      context: this.closest("quick-add-dialog") ? "dialog" : "product",
      lines: [
        {
          merchandiseId: String(formData.get("id")),
          quantity: Number(formData.get("quantity")) || Number(this.dataset.quantityDefault) || 1,
        },
      ],
    });

    fetch(FoxTheme.routes.cart_add_url, {
      ...fetchCfg,
      headers: {
        ...fetchCfg.headers,
        Accept: "text/html",
      },
    })
      .then((response) => response.json())
      .then((response) => {
        if (response.status) {
          // The cart declined the add (sold out, over the limit…): resolve with the cart as it stands.
          storefrontEvents.resolveCart(linesUpdate, null, storefrontEvents.getAjaxError(response));

          this.dispatchEvent(
            new CartErrorEvent(form.getAttribute("id") || "", response.message, response.description, response.errors)
          );

          document.dispatchEvent(
            new CustomEvent(ThemeEvents.productAjaxError, {
              detail: { errorMessage: response.description || response.message },
            })
          );

          this.refs.addToCartButtonContainer?.removeLoading();

          if (!addToCartTextError) return;

          // Clear existing timeout to reset the 5s timer
          if (this.#timeout) {
            clearTimeout(this.#timeout);
          }

          // Set error message (replaces any existing message)
          addToCartTextError.textContent = response.description || response.message;
          addToCartTextError.classList.remove("hidden");

          // Announce error for screen readers
          this.#setLiveRegionText(response.message);

          // Hide error message after 5 seconds
          this.#timeout = setTimeout(() => {
            if (!addToCartTextError) return;
            addToCartTextError.classList.add("hidden");
            this.#clearLiveRegionText();
          }, 5000);

          // When error occurs (e.g. adding more items than available),
          // backend still adds the max allowed amount to cart.
          // Dispatch CartUpdateEvent with actual count for cart count sync.
          if (response.sections) {
            let actualItemCount = 0;
            let foundCount = false;

            for (const section of Object.values(response.sections)) {
              const tempDiv = document.createElement("div");
              tempDiv.innerHTML = section;

              // Try cart-items ref first, fallback to cart-count bubble
              let itemCountElement = tempDiv.querySelector('[ref="cartItemCount"]');
              if (!itemCountElement) {
                itemCountElement = tempDiv.querySelector(".cart-bubble__text-count");
              }

              if (itemCountElement) {
                actualItemCount = parseInt(itemCountElement.textContent || "0", 10);
                foundCount = true;
                break;
              }
            }
            if (foundCount) {
              this.dispatchEvent(
                new CartUpdateEvent({}, this.id, {
                  itemCount: actualItemCount,
                  source: "product-form-component",
                  productId: this.dataset.productId,
                  sections: response.sections,
                })
              );
            }
          } else {
            // Shopify doesn't return sections on 422 errors
            // Fetch cart.js AND section HTML in parallel for instant morph
            const sectionsData = {};
            const sectionPromises = this.#gatherGroupedSectionIds().map(async (sectionId) => {
              const sectionUrl = `${window.location.pathname.split("?")[0]}?section_id=${sectionId}`;
              const res = await fetch(sectionUrl);
              sectionsData[sectionId] = await res.text();
            });

            const cartPromise = fetch(FoxTheme.routes.cart).then((res) => res.json());

            Promise.all([cartPromise, ...sectionPromises])
              .then(([cart]) => {
                this.dispatchEvent(
                  new CartUpdateEvent(cart, this.id, {
                    itemCount: cart.item_count || 0,
                    source: "product-form-component",
                    productId: this.dataset.productId,
                    sections: Object.keys(sectionsData).length > 0 ? sectionsData : undefined,
                  })
                );
              })
              .catch((error) => {
                console.error("Failed to fetch cart count:", error);
              });
          }

          return;
        } else {
          const id = formData.get("id");

          if (addToCartTextError) {
            addToCartTextError.classList.add("hidden");
            addToCartTextError.removeAttribute("aria-live");
          }

          if (!id) throw new Error("Form ID is required");

          // `add.js` answers with the added items, not the cart: resolveCart() fetches it.
          storefrontEvents.resolveCart(linesUpdate, null);

          // Add aria-live region to inform screen readers that the item was added
          if (this.refs.addToCartButtonContainer?.refs.addToCartButton) {
            const addedText = FoxTheme.translations.added;
            this.#setLiveRegionText(addedText);

            setTimeout(() => {
              this.#clearLiveRegionText();
            }, 5000);
          }

          this.dispatchEvent(
            new CartAddEvent(response, id.toString(), {
              source: "product-form-component",
              itemCount: Number(formData.get("quantity")) || Number(this.dataset.quantityDefault),
              productId: this.dataset.productId,
              sections: response.sections,
            })
          );

          document.dispatchEvent(
            new CustomEvent(ThemeEvents.productAjaxAdded, {
              detail: { product: response },
            })
          );
        }
      })
      .catch((error) => {
        storefrontEvents.failCart(this, linesUpdate, error);
        this.refs.addToCartButtonContainer?.removeLoading();
        console.error(error);
      })
      .finally(() => {
        // add more thing to do in here if needed.
        // cartPerformance.measureFromEvent("add:user-action", event);
      });
  }

  /**
   * @param {*} text
   */
  #setLiveRegionText(text) {
    const liveRegion = this.refs.liveRegion;
    liveRegion.textContent = text;
  }

  #clearLiveRegionText() {
    const liveRegion = this.refs.liveRegion;
    liveRegion.textContent = "";
  }

  /**
   * Subscription apps (Shopify Subscriptions) render purchase options for every variant and switch to the
   * selected variant's options, updating the form's `selling_plan`, when the variant input's `value` attribute
   * changes (setting the property alone isn't observable). Keeps the buyer's purchase option when the new variant
   * offers it.
   * @param {string} previousVariantId
   * @param {string} variantId
   */
  #syncPurchaseOptions(previousVariantId, variantId) {
    const section = this.closest(".shopify-section, dialog");
    const previous =
      previousVariantId !== variantId
        ? section?.querySelector(
            `[data-variant-id="${previousVariantId}"] input[type="radio"][data-radio-type]:checked`
          )
        : null;

    this.refs.variantId.setAttribute("value", variantId);

    if (!section || !variantId || !(previous instanceof HTMLInputElement)) return;
    const { radioType, sellingPlanId } = previous.dataset;
    // Once the widget has switched to the new variant's options (in its MutationObserver callback), pick the same
    // option the way the buyer does, so the widget updates the form's `selling_plan` and its own state.
    setTimeout(() => this.#selectPurchaseOption(section, variantId, radioType, sellingPlanId));
  }

  /**
   * @param {Element} section
   * @param {string} variantId
   * @param {string | undefined} radioType
   * @param {string | undefined} sellingPlanId
   */
  #selectPurchaseOption(section, variantId, radioType, sellingPlanId) {
    const options = `[data-variant-id="${variantId}"]`;

    // A plan picked in a group's dropdown ("Dropdown" selector): pick it there, the widget checks the group.
    const planOption = sellingPlanId
      ? section.querySelector(`${options} select option[value="${sellingPlanId}"]`)
      : null;
    const dropdown = planOption?.closest("select");
    if (dropdown instanceof HTMLSelectElement) {
      dropdown.value = sellingPlanId;
      dropdown.dispatchEvent(new Event("change", { bubbles: true }));
      return;
    }

    // "Toggle" style: the one-time / subscribe tab comes first, the plans are disabled under the one-time tab.
    const tabPrefix = radioType === "one_time_purchase" ? "tab_otp_" : "tab_subscribe_";
    const tab = section.querySelector(`input[type="radio"][id^="${tabPrefix}"][id$="_${variantId}"]`);
    if (tab instanceof HTMLInputElement && !tab.checked) tab.click();

    const radio = Array.from(
      section.querySelectorAll(`${options} input[type="radio"][data-radio-type="${radioType}"]`)
    ).find((input) => input instanceof HTMLInputElement && input.dataset.sellingPlanId === sellingPlanId);
    if (radio instanceof HTMLInputElement && !radio.checked) radio.click();
  }

  /**
   * @param {VariantUpdateEvent} event
   */
  #onVariantUpdate = (event) => {
    if (event.detail.data.newProduct) {
      this.dataset.productId = event.detail.data.newProduct.id;
    } else if (event.detail.data.productId !== this.dataset.productId) {
      return;
    }

    const { variantId, addToCartButtonContainer } = this.refs;

    const variant = event.detail.resource;
    const isAvailable = variant?.available ?? false;

    // The variant ID and accelerated checkout don't depend on the add to cart button: it may be hidden
    // (block disabled in the editor) while the dynamic checkout buttons still submit this form.
    const previousVariantId = variantId.value;
    variantId.value = variant?.id ?? "";
    this.#syncPurchaseOptions(previousVariantId, variantId.value);
    this.refs.acceleratedCheckoutButtonContainer?.toggleAttribute("hidden", !isAvailable);

    // Card quick add sends the variant's minimum quantity (snippets/quick-add.liquid).
    const quantityInput = this.querySelector('input[type="hidden"][name="quantity"]');
    if (variant && quantityInput instanceof HTMLInputElement) {
      quantityInput.value = String(variant.quantity_rule?.min || 1);
    }

    const currentAddToCartButton = addToCartButtonContainer?.refs.addToCartButton;

    if (!currentAddToCartButton) return;

    // Determine correct unavailable state
    // Sold out: Has inventory management (shopify) → inventory = 0
    // Unavailable: No inventory management OR variant is null/undefined → other reasons
    const isSoldOut = !isAvailable && variant && variant.inventory_management === "shopify";

    if (!isAvailable) {
      addToCartButtonContainer.disable();
    } else {
      addToCartButtonContainer.enable();
    }

    // Update button text optimistically (works for both available and unavailable)
    // This handles ALL cases: cache hit, JSON fetch, and HTML fallback
    this.#updateButtonTextOptimistic(currentAddToCartButton, isAvailable, isSoldOut);
  };

  /**
   * Updates button text optimistically based on variant availability
   * @param {HTMLElement} button - The add to cart button element
   * @param {boolean} isAvailable - Whether the variant is available
   * @param {boolean} isSoldOut - Whether the variant is sold out (has inventory_management = "shopify")
   */
  #updateButtonTextOptimistic(button, isAvailable, isSoldOut = false) {
    if (!button) return;

    const textElement = button.querySelector(".add-to-cart-text__content");
    const iconTextElement = button.querySelector(".btn__icon-text");

    if (!textElement) return;

    // Get text from data attributes (translations from Liquid)
    const availableText = button.dataset.addToCartText || "Add to cart";
    const soldOutText = button.dataset.soldOutText || "Sold out";
    const unavailableText = button.dataset.unavailableText || "Unavailable";

    // Determine correct text based on state
    // - Available: variant.available = true → "Add to cart"
    // - Sold out: variant.available = false + inventory_management = "shopify" → "Sold out"
    // - Unavailable: variant.available = false + no inventory_management → "Unavailable"
    let newText;
    if (isAvailable) {
      newText = availableText;
    } else if (isSoldOut) {
      newText = soldOutText;
    } else {
      newText = unavailableText;
    }

    // Update both text content and icon text (if present)
    textElement.textContent = newText;
    if (iconTextElement) {
      iconTextElement.textContent = newText;
    }
  }

  /**
   * Disable the add to cart button while the UI is updating before #onVariantUpdate is called.
   * Accelerated checkout button is also disabled via its own event listener not exposed to the theme.
   */
  #onVariantSelected = () => {
    this.refs.addToCartButtonContainer?.disable();
  };
}

if (!customElements.get("product-form-component")) {
  customElements.define("product-form-component", ProductFormComponent);
}
